import clsx from "clsx";
import { FC, RefObject, useMemo, useState } from "react";

import type { EvalRetryError } from "@tsmono/inspect-common";
import {
  TranscriptCollapseState,
  TranscriptLayout,
} from "@tsmono/inspect-components/transcript";
import {
  ANSIDisplay,
  ExpandablePanel,
  Segment,
  SegmentedControl,
} from "@tsmono/react/components";
import { Icon } from "@tsmono/react/icons";
import { formatDateTime } from "@tsmono/util";

import { attemptStartTime, deriveErrorType } from "./retryAttempt";
import styles from "./RetryAttemptCard.module.css";

export type RetryView = "error" | "events";

const isRetryView = (value: string): value is RetryView =>
  value === "error" || value === "events";

const kViewSegments: Segment[] = [
  { id: "error", label: "Error", icon: "warning" },
  { id: "events", label: "Events", icon: "listView" },
];

export interface RetryAttemptCardProps {
  retry: EvalRetryError;
  attemptNumber: number;
  isOpen: boolean;
  onToggleOpen: () => void;
  listId: string;
  scrollRef: RefObject<HTMLDivElement | null>;
}

export const RetryAttemptCard: FC<RetryAttemptCardProps> = ({
  retry,
  attemptNumber,
  isOpen,
  onToggleOpen,
  listId,
  scrollRef,
}) => {
  const errorType = useMemo(() => deriveErrorType(retry), [retry]);
  const startTime = useMemo(() => attemptStartTime(retry), [retry]);
  const hasEvents = !!retry.events?.length;
  const [view, setView] = useState<RetryView>("error");

  return (
    <div
      className={clsx(
        styles.card,
        isOpen ? styles.cardOpen : styles.cardCollapsed
      )}
    >
      <div
        className={styles.header}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={onToggleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleOpen();
          }
        }}
      >
        <span
          className={styles.attemptLabel}
        >{`Attempt ${attemptNumber}`}</span>
        {errorType && <span className={styles.errorChip}>{errorType}</span>}
        {retry.message && (
          <span className={styles.message}>{retry.message}</span>
        )}
        {startTime && (
          <span className={styles.timestamp}>{formatDateTime(startTime)}</span>
        )}
        <Icon
          name={isOpen ? "chevronDown" : "chevronRight"}
          className={styles.chevron}
        />
      </div>

      {isOpen && (
        <div className={styles.body}>
          {hasEvents && (
            // Error/Events control lives on a hairline divider band at the top
            // of the body (not the header) so it stays put under a long
            // traceback instead of scrolling away.
            <div className={styles.dividerBand}>
              <span className={styles.hairline} aria-hidden="true" />
              <div className={styles.toggle}>
                <SegmentedControl
                  segments={kViewSegments}
                  selectedId={view}
                  onSegmentChange={(id) => {
                    if (isRetryView(id)) setView(id);
                  }}
                />
              </div>
              <span className={styles.hairline} aria-hidden="true" />
            </div>
          )}
          <div className={styles.content}>
            {view === "error" || !hasEvents ? (
              <ExpandablePanel
                id={`retry-error-${listId}`}
                collapse={true}
                className={styles.errorPanel}
              >
                <ANSIDisplay
                  output={retry.traceback_ansi}
                  className={styles.ansi}
                />
              </ExpandablePanel>
            ) : (
              <RetryEventsView
                retry={retry}
                listId={listId}
                scrollRef={scrollRef}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RetryEventsView: FC<{
  retry: EvalRetryError;
  listId: string;
  scrollRef: RefObject<HTMLDivElement | null>;
}> = ({ retry, listId, scrollRef }) => {
  // Pre-seed collapsed state for state/store events so their collapsibleContent
  // bodies start hidden — mirrors the main transcript, where they're hidden
  // inside collapsed parent containers.
  const initialCollapsed = useMemo(() => {
    const ids: Record<string, boolean> = {};
    for (const event of retry.events || []) {
      if ((event.event === "state" || event.event === "store") && event.uuid) {
        ids[event.uuid] = true;
      }
    }
    return ids;
  }, [retry.events]);

  const [transcriptCollapsed, setTranscriptCollapsed] = useState<
    Record<string, boolean> | undefined
  >(undefined);
  const [bulkCollapse, setBulkCollapse] = useState<
    "collapse" | "expand" | undefined
  >("expand");

  const effectiveCollapsed = useMemo(
    () =>
      transcriptCollapsed
        ? { ...initialCollapsed, ...transcriptCollapsed }
        : Object.keys(initialCollapsed).length > 0
          ? initialCollapsed
          : undefined,
    [transcriptCollapsed, initialCollapsed]
  );

  const collapseState = useMemo<TranscriptCollapseState>(
    () => ({
      transcript: effectiveCollapsed,
      onCollapseTranscript: (nodeId: string, collapsed: boolean) =>
        setTranscriptCollapsed((prev) => ({ ...prev, [nodeId]: collapsed })),
      onSetTranscriptCollapsed: (ids: Record<string, boolean>) => {
        setTranscriptCollapsed(ids);
        setBulkCollapse(undefined);
      },
    }),
    [effectiveCollapsed]
  );

  return (
    <div className="text-size-small">
      <TranscriptLayout
        events={retry.events || []}
        scrollRef={scrollRef}
        listId={listId}
        embedded
        timeline={{ showSwimlanes: false }}
        collapseState={collapseState}
        bulkCollapse={bulkCollapse}
        eventNodeContext={{ inlineExpansionUX: true }}
        keyboardNavDisabled={true}
      />
    </div>
  );
};
